import { useEffect, useMemo, useRef, useState } from 'react'
import { CalendarIcon, Pen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { AutoComplete, type Option } from "@/components/styled-autocomplete.tsx"
import { AddNonMedical } from '@/components/add-non-medical';
import { useDebounce } from '@uidotdev/usehooks';
import ActionAndInfo from './action-and-info'
import { AddedItemsSchema } from '../-data/schema';
import { NumericString, numericStringDefault, stringToNumericString } from '@/utils/numeric_string';
import { LoadState } from '@/utils/load_state';
import { Loading } from '@/layouts/loading';
import dayjs from 'dayjs';
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useMutation } from '@connectrpc/connect-query';
import { generateCustomBarcode, getPriceHistory, searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery';
import { SearchInventoryRequest } from '@/gen/search/search_inventory_pb';
import { DiscountType, ItemType } from '@/gen/inventory/common_pb';
import { GetPriceHistoryRequest } from '@/gen/medical_master/get_price_history_pb';
import { GenerateCustomBarcodeRequest } from '@/gen/inventory/generate_custom_barcode_pb';
import { TaxProfile } from '@/gen/medical_master/common_pb';
import { pharmacyClient } from '@/connect';
dayjs.extend(customParseFormat);
import { GS1Reader } from 'gs1js';
import BarcodeModal from './BarcodeModal';
import getPharmacyID from '@/utils/pharmacy_code';

export type AddItemForm = {
  item_id: string,
  trade_name: string,
  type: "MEDICAL" | "NON_MEDICAL",
  search: Option
  batchNo: string,
  expiryDate?: string,
  productionDate?: string,
  cost: NumericString,
  price: NumericString,
  customBarcode: string,
  discountType: string,
  discountValue: NumericString,
  quantity: NumericString,
  bonus: NumericString,
  taxProfile: TaxProfile
  serial_number?: string
  scientific_name?: string,
  unit?: string,
  unitType?: string,
}

export const checkDuplicateBatch = (
  items: AddedItemsSchema[],
  batchNo: string,
  id?: string,
  type?: "MEDICAL" | "NON_MEDICAL",
): boolean => {
  let count = 0;
  for (let item of items) {
    if (item.batch_number === batchNo && item.medical_master_id === id && type === "MEDICAL") {
      count++;
    }
    if (item.batch_number === batchNo && item.non_medical_master_id === id && type === "NON_MEDICAL") {
      count++;
    }
    if (count > 1) {
      return true
    }
  }
  return false;
};

export const formValidator = (data: AddItemForm, items: AddedItemsSchema[], mode: "ADD" | "EDIT"): {
  status: boolean,
  message: string,
} => {
  if (data.trade_name === "") {
    return {
      status: false,
      message: "Trade name is required"
    }
  }
  if (data.batchNo === "") {
    return {
      status: false,
      message: "Batch number is required"
    }
  }
  if (data.cost.value <= 0) {
    return {
      status: false,
      message: "Cost must be positive"
    }
  }
  //if (data.productionDate === undefined) {
   // return {
    //  status: false,
    //  message: "Production date is required"
   // }
  // }
  if (data.type === "NON_MEDICAL" && (data.price.value === 0 || data.price.value === undefined)) {
    return {
      status: false,
      message: "Price is required for non-medical products"
    }
  }
  if (data.type === "MEDICAL" && data.expiryDate === undefined) {
    return {
      status: false,
      message: "Expiry date is required for medical products"
    }
  }
  if (data.quantity.value <= 0) {
    return {
      status: false,
      message: "Original quantity must be more than 0"
    }
  }
  if (data.bonus.value < 0) {
    return {
      status: false,
      message: "Bonus quantity must be greater or equal to 0"
    }
  }
  if (data.cost.value < 0) {
    return {
      status: false,
      message: "Cost must be greater or equal to 0"
    }
  }
  if (data.discountType !== DiscountType.NONE.toString() && data.discountValue.value <= 0) {
    return {
      status: false,
      message: "Discount value must be greater than 0"
    }
  }
  if (mode === "ADD") {
    if (checkDuplicateBatch(items, data.batchNo, data.item_id, data.type as "MEDICAL" | "NON_MEDICAL")) {
      return {
        status: false,
        message: "Duplicate batch number"
      }
    }

  }
  return {
    status: true,
    message: ""
  };
}


export const addedItemsValidator = (globalItems: AddedItemsSchema[]) => {
  if (globalItems.length == 0) {
    return false
  }
  for (let item of globalItems) {
    //Quantity original should not be 0 bonus can be 0
    //Cost must be greater than 0 for both
    //Batch number is compulsory
    if (item.cost === 0 || item.quantity.original === 0 || item.batch_number === "" || item.trade_name === "" || (item.metadata.discount_type !== DiscountType.NONE.toString() && item.metadata.discount_value === 0)) {
      return false
    }
    //Non medical items price should not be 0
    if (item.type === "NON_MEDICAL") {
      if (item.price === undefined || item.price === 0) {
        return false
      }
    }
    // Production date optional for both medical and non medical
    if (item.production_date !== undefined) {
      //Production date should not be in the future
      if (dayjs(item.production_date).startOf('day').isAfter(dayjs().startOf('day'))) {
        return false
      }
    }
    //Expiry date compulsory for medical items optional for non medical
    if (item.expiry_date === undefined) {
      if (item.type === "MEDICAL") {
        return false
      }
    } else {
      //Expiry date should not be in the past or before production date
      const expiry_date = dayjs(item.expiry_date).startOf('day')
      if (expiry_date.isBefore(dayjs().startOf('day'))) {
        return false
      }
      if (item.production_date) {
        const production_date = dayjs(item.production_date).startOf('day')
        if (expiry_date.isBefore(production_date)) {
          return false
        }
      }
    }
    // no duplicate batch numbers
    if (checkDuplicateBatch(globalItems, item.batch_number, item.type === "MEDICAL" ? item.medical_master_id : item.non_medical_master_id, item.type)) {
      return false
    }
  }
  return true
}

export const AddedItemToForm = (item: AddedItemsSchema): AddItemForm => {
  const id = (item.medical_master_id || item.non_medical_master_id)!;
  return {
    item_id: id,
    trade_name: item.trade_name,
    type: item.medical_master_id ? "MEDICAL" : "NON_MEDICAL",
    search: {
      label: item.trade_name,
      value: id,
      type: item.type,
      tax_profile: item.taxProfile?.toJsonString() || "",
    },
    batchNo: item.batch_number,
    cost: {
      value: item.cost,
      strValue: item.cost.toString()
    },
    price: {
      value: item.price || 0,
      strValue: item.price?.toString() || "0"
    },
    expiryDate: item?.expiry_date?.toString() || undefined,
    productionDate: item?.production_date?.toString() || undefined,
    customBarcode: item.metadata.custom_barcode || "",
    discountType: item.metadata.discount_type || "NONE",
    discountValue: {
      value: item.metadata.discount_value || 0,
      strValue: item.metadata.discount_value?.toString() || "0"
    },
    quantity: {
      value: item.quantity.original,
      strValue: item.quantity.original.toString()
    },
    bonus: {
      value: item.quantity.bonus,
      strValue: item.quantity.bonus.toString()
    },
    taxProfile: item.taxProfile
  }
}

const Add = ({ items, editItem, addItem, injectItem, resetInjectItem }: {
  items: AddedItemsSchema[]
  addItem: (newItem: AddItemForm) => void
  editItem: (newItem: AddItemForm, dbItemId: number) => void
  injectItem?: AddedItemsSchema
  resetInjectItem?: () => void
}) => {
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<Option[]>([]);
  const debouncedSearchTerm = useDebounce(search, 300);

  const defaultFormObj: AddItemForm = {
    item_id: "",
    trade_name: "",
    type: "MEDICAL",
    search: {
      label: "",
      value: ""
    },
    batchNo: "",
    cost: numericStringDefault(),
    price: numericStringDefault(),
    expiryDate: undefined,
    productionDate: undefined,
    customBarcode: "",
    discountType: "NONE",
    taxProfile: new TaxProfile(),
    discountValue: numericStringDefault(),
    quantity: numericStringDefault(),
    bonus: numericStringDefault()
  }
  const [formObj, setFormObj] = useState(defaultFormObj)
  const formDisabled = useMemo(() => formObj.trade_name === "", [formObj.trade_name])
  const [mode, setMode] = useState<"ADD" | "EDIT">(injectItem ? "EDIT" : "ADD")
  const [loadingDetails, setLoadingDetails] = useState(LoadState.None)
  const { status: formValidity, message: errorMessage } = useMemo(() => {
    return formValidator(formObj, items, mode)
  }, [formObj, items])


  const resetForm = () => {
    setFormObj(defaultFormObj);
    setSearch('');
    resetInjectItem && resetInjectItem();
  };

  const searchMutation = useMutation(searchInventory, {
    onSuccess: (data) => {
        setResults(data.items.map((item) => ({ label: item.tradeName + " " + item.unitType + ":::" + item.manufacturer + ":::" + item.owner, value: item.id, type: item.type.toString(), barcode: item.barcode,
          trade_name: item.tradeName,
          unit: item.unit.toString(),
          unit_type: item.unitType,
          manufacturer: item.manufacturer,
          owner: item.owner,
          is_suggestion: item.isSuggestion ? 'yes' : 'no',
          ranking_score: item.rankingScore.toString(),
          available_stock: item.availableStock.toString(),
          is_add: "yes",
          tax_profile: item.taxProfile?.toJsonString() || "",
        })));
    },
    onError: (error) => {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to search for items. Please try again.",
        duration: 5000,
      })
    },
  })

  const pricingHistoryMutation = useMutation(getPriceHistory, {
    onSuccess: (data) => {
      const price = data.priceHistories.length > 0 ? data.priceHistories.filter(item => item.isCurrentActivePriceForPharmacy).length > 0 ? data.priceHistories.filter(item => item.isCurrentActivePriceForPharmacy)[0].price : data.priceHistories[0].price : 0;
      setFormObj({
        ...formObj,
        price: {
          value: price,
          strValue: price.toString()
        }
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to fetch price history. Please try again.",
        duration: 5000,
      });
    }
  });

  const generateBarcodeMutation = useMutation(generateCustomBarcode, {
    onSuccess: (data) => {
      setFormObj({
        ...formObj,
        customBarcode: data.barcode
      });
      setCustomBarcodeGenerating(false);
    },
    onError: (error) => {
      setCustomBarcodeGenerating(false);
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to generate custom barcode. Please try again.",
        duration: 5000,
      });
    }
  })

  const onSubmit = (formObj: AddItemForm) => {
    if (!formValidity) {
      toast({
        title: "Error",
        variant: "destructive",
        description: errorMessage,
        duration: 5000,
      });
      return;
    }
    if (mode === "ADD") {
      addItem(formObj);
    }
    if (mode === "EDIT") {
      editItem(formObj, injectItem!.db_item_id);
    }
    resetForm();
    setMode("ADD");
  };

  useEffect(() => {
    if (debouncedSearchTerm.length === 0) {
      setResults([]);
      return;
    }
    if (debouncedSearchTerm.length < 3) return;    
    const inputData = new SearchInventoryRequest();
    inputData.onlyAvailable = false;
    inputData.query = debouncedSearchTerm;
    inputData.limit = 5;
    searchMutation.mutate(inputData);          
  }, [debouncedSearchTerm]);


  useEffect(() => {
    let price = injectItem?.price;
    if (injectItem) {
      if (injectItem.type === "MEDICAL" && !price) {
        setLoadingDetails(LoadState.Loading)
        pharmacyClient.getPriceHistory({
          id: injectItem.medical_master_id,
          page: 1,
          pageSize: 10000
        }).then((data) => {
          const priceHistory = data.priceHistories;
          if (priceHistory.length > 0) {
            const currentPrice = priceHistory.find(item => item.isCurrentActivePriceForPharmacy);
            price = currentPrice ? currentPrice.price : priceHistory[0].price;
          } else {
            price = 0;
          }
          setFormObj(AddedItemToForm({ ...injectItem, price: price }));
          setLoadingDetails(LoadState.Loaded);
          setMode("EDIT");
        }).catch((error) => {
          console.error("Error fetching price history:", error);
          setLoadingDetails(LoadState.Error)
          toast({
            title: "Error",
            variant: "destructive",
            description: "Failed to fetch price history. Please try again.",
            duration: 5000,
          });
        })
      } else {
        setMode("EDIT");
        setFormObj(AddedItemToForm({ ...injectItem, price: price }));

      }
    }
  }, [injectItem])

  const [customBarcodeGenerating, setCustomBarcodeGenerating] = useState(false);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [dataMatrixCode, setDataMatrixCode] = useState<string | null>(null);
  const autoCompleteRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    interface KeystrokeData {
      key: string;
      code: string;
      timestamp: number;
      altKey?: boolean;
      ctrlKey?: boolean;
      shiftKey?: boolean;
    }
  
    let keystrokeBuffer: KeystrokeData[] = [];
    let scanTimer: ReturnType<typeof setTimeout> | null = null;
    let isScanning = false;
  
    const captureRawKeystroke = (event: KeyboardEvent) => {
      
      const now = Date.now();
      const timeSinceLastKey = keystrokeBuffer.length > 0 ? 
        now - keystrokeBuffer[keystrokeBuffer.length - 1].timestamp : 0;
      

      // Start scanning on rapid keystrokes and continue scanning if already scanning
      if ((timeSinceLastKey < 50 && timeSinceLastKey > 0) || isScanning ){
        isScanning = true;
      }
  
      if (keystrokeBuffer.length === 0 && event.key.length === 1) {
        isScanning = true;
      }
  
      if (isScanning) {
        keystrokeBuffer.push({
          key: event.key,
          code: event.code,
          timestamp: now,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey
        });
        
        // Only preventDefault on scanner-specific keystrokes
        const isScannerKeystroke = (
          // Function keys used as separators
          event.key === 'F1' || event.key === 'F7' || event.key === 'F8' || 
          event.key === 'F11' || event.key === 'F12' ||
          // Control combinations
          (event.ctrlKey && (event.key === ']' || event.key === '+')) ||
          // Separator substitutes
          event.key === '~' || event.key === '|' ||
          // Other scanner-specific keys
          event.code === 'Oem6'
        );
        
        if (isScannerKeystroke) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        if (scanTimer) clearTimeout(scanTimer);


        const isSpecialKey = event.key === 'Alt' || event.key === '≠' || event.altKey || event.shiftKey;
        const timeoutDelay = isSpecialKey ? 500 : 150; // Longer timeout for special keys

        scanTimer = setTimeout(() => {
          const reconstructedString = reconstructString(keystrokeBuffer);
          
          // Test the parsing
          testParser(reconstructedString);
          
          // Try actual form update
          if (reconstructedString.length > 10) {
            updateFormWithScannedData(reconstructedString);
          }
          
          // Reset
          keystrokeBuffer = [];
          isScanning = false;
        }, timeoutDelay);
      }
    };
  
    const reconstructString = (keystrokes: KeystrokeData[]): string => {
      let result = '';
      let i = 0;
      
      while (i < keystrokes.length) {
        const k = keystrokes[i];
        
        // Handle GS1 separator keystrokes
        if (
          k.key === 'F1' || k.key === 'F7' || k.key === 'F8' || 
          k.key === 'F11' || k.key === 'F12'
        ) {
          result += '\u001D'; // Convert to GS1 separator
        }
        // Alt+0029 sequence detection
        else if (k.altKey && k.key === '0' && i < keystrokes.length - 3) {
          
          const next1 = keystrokes[i + 1];
          const next2 = keystrokes[i + 2]; 
          const next3 = keystrokes[i + 3];
          
          
          // Check for Alt+0+0+2+9 or Alt+0+2+9 patterns
          if (next1?.key === '0' && next2?.key === '2' && next3?.key === '9') {
            result += '\u001D';
            i += 3; // Skip the 0,2,9 keys
          }
          else if (i < keystrokes.length - 4 && next1?.key === '0' && next2?.key === '0' && next3?.key === '2' && keystrokes[i + 4]?.key === '9') {
            result += '\u001D';
            i += 4; // Skip the 0,0,2,9 keys
          }
          else {
            result += '0';
          }
        }
        else if (k.key === 'Alt' || k.key === '≠' || (k.altKey && k.key !== '0')) {
          
          // Look for Alt+0029 pattern
          if (i < keystrokes.length - 3) {
            const next1 = keystrokes[i + 1];
            const next2 = keystrokes[i + 2]; 
            const next3 = keystrokes[i + 3];
            
            
            if (next1?.key === '0' && next2?.key === '2' && next3?.key === '9') {
              result += '\u001D';
              i += 3; // Skip the 0, 2, 9
            } else {
            }
          } else {
          }
        }
        // Ctrl key combinations
        else if (i < keystrokes.length - 1) {
          const nextKey = keystrokes[i + 1];
          
          if ((k.key === 'Control' && nextKey?.key === ']') || 
              (k.key === ']' && keystrokes.some(ks => ks.key === 'Control'))) {
            result += '\u001D';
            if (k.key === 'Control') i++;
          }
          else if ((k.key === 'Control' && nextKey?.key === '+') || 
                   (k.key === '+' && keystrokes.some(ks => ks.key === 'Control'))) {
            result += '\u001D';
            if (k.key === 'Control') i++;
          }
          else if (k.key === 'Control') {
          } else {
            if (k.key.length === 1 && /^[a-zA-Z0-9]$/.test(k.key)) {
              result += k.key;
            }
          }
        }
        // Printable substitutes
        else if (k.key === '~' || k.key === '|') {
          result += '\u001D';
        }
        // Datalogic Oem6 key
        else if (k.code === 'Oem6' || k.key === 'Oem6') {
          result += '\u001D';
        }
        // Regular alphanumeric characters
        else if (k.key.length === 1 && /^[a-zA-Z0-9]$/.test(k.key)) {
          result += k.key;
        }
        // Handle Shift+Letter combinations
        else if (k.shiftKey && k.key.length === 1 && /^[A-Z]$/.test(k.key)) {
          result += k.key;
        }
        i++;
      }
      
      return result.replace(/Enter$/, '');
    };
  
    const testParser = (dataMatrix: string) => {
      
      const result: Record<string, string> = {};
      const gs = new GS1Reader(dataMatrix);
      
      if (gs.lookup["01"]) {
        const gtin = gs.lookup["01"].value;
        result['01'] = gtin;
        
        if(gs.lookup["17"]) {
          const expiry = gs.lookup["17"].value;
          result['17'] = expiry;
        }
  
        if(gs.lookup["10"]) {
          const batch = gs.lookup["10"].value;
          result['10'] = batch;
        }
  
        if(gs.lookup["21"]) {
          const serial = gs.lookup["21"].value;
          result['21'] = serial;
        }
  
      } else {
        console.log('No GTIN found in GS1 data');
      }
      
      return result;
    };
  
    const updateFormWithScannedData = (dataMatrixString: string) => {
      try {
        
        const parsed = testParser(dataMatrixString);
        
        if (parsed['01']) {
          setSearch(parsed['01']);
          
          /*setTimeout(() => {
            if (autoCompleteRef.current) {
              console.log('Focusing autocomplete');
              autoCompleteRef.current.focus();
              autoCompleteRef.current.click();
            }
          }, 100);*/
          
          let expiryDate: string | undefined = undefined;
          if (parsed['17'] && parsed['17'].length === 6 && /^\d{6}$/.test(parsed['17'])) {
            try {
              const parsedExpiry = dayjs(parsed['17'], "YYMMDD");
              if (parsedExpiry.isValid()) {
                expiryDate = parsedExpiry.format("YYYY-MM-DD");
              }
            } catch (error) {
              console.error("Error parsing expiry date:", error);
            }
          }
          
          setFormObj(prevForm => {
            const newForm = {
              ...prevForm,
              batchNo: parsed['10'] || "",
              expiryDate: expiryDate,
              serial_number: parsed['21']
            };
            
            return newForm;
          });
          
          setDataMatrixCode(dataMatrixString);
          setTimeout(() => setDataMatrixCode(null), 2000);
          
        } else {
          console.warn('No GTIN found in parsed data');
        }
      } catch (error) {
        console.error('Form update failed:', error);
      }
    };
  
    // Add event listener
    document.addEventListener('keydown', captureRawKeystroke, true);
    
    return () => {
      document.removeEventListener('keydown', captureRawKeystroke, true);
      if (scanTimer) clearTimeout(scanTimer);
    };
  }, []);

  return (
    <div className='bg-[#F6F6F6] rounded-sm p-4 h-full overflow-auto'>
      {
        loadingDetails === LoadState.Loading &&
        <div className='w-full h-full flex items-center justify-center'>
          <Loading />
        </div>
      }
      {
        loadingDetails === LoadState.Error &&
        <div className='w-full h-full flex items-center justify-center'>
          <p className='text-red-500'>Failed to load item details</p>
        </div>
      }
      {
        loadingDetails !== LoadState.Loading &&
        <form onSubmit={(e) => {
          e.preventDefault()
          onSubmit(formObj)
        }} className="w-full flex flex-col gap-4 mt-4 mb-24">
          <div className='flex items-center justify-between'>
            <p className="text-sm font-semibold">
              Product Name<span className="text-red-400">*</span>
            </p>
            <AddNonMedical />
          </div>
          <div className='space-y-2'>
            <div className='border rounded-md px-2 flex items-center bg-white'>
              {
                mode == "EDIT" ?
                  <div className='w-full flex flex-row gap-2 min-h-11 py-1 items-center'>
                    <div>
                      <Pen className='w-4 h-4' />
                    </div>
                    <p>Editing info for {formObj.trade_name}</p>
                  </div>
                  :
                  <AutoComplete
                    options={results}
                    emptyMessage="No results."
                    placeholder="Search for a product"
                    onValueChange={(value) => {
                      if (value?.type === ItemType.MEDICAL.toString()) {
                        setFormObj({
                            ...formObj,
                            item_id: value.value,
                            trade_name: value.trade_name,
                            type: value.type === ItemType.MEDICAL.toString() ? "MEDICAL" : "NON_MEDICAL",
                            search: value,
                            taxProfile: TaxProfile.fromJsonString(value.tax_profile || ""),
                        });
                        const pricingHistoryData = new GetPriceHistoryRequest();
                        pricingHistoryData.id = value.value;
                        pricingHistoryData.page = 1;
                        pricingHistoryData.pageSize = 10000;
                        pricingHistoryMutation.mutate(pricingHistoryData);
                      }
                      if (!value) {
                        setSearch('');
                        setFormObj(defaultFormObj);
                      }
                      setFormObj({
                        ...formObj,
                        item_id: value.value,
                        trade_name: value.trade_name,
                        type: value.type === ItemType.MEDICAL.toString() ? "MEDICAL" : "NON_MEDICAL",
                        search: value,
                        taxProfile: TaxProfile.fromJsonString(value.tax_profile || ""),
                      })
                    }}
                    value={formObj.search}
                    onInputChanged={(value) => {
                      setSearch(value);
                      if (!value) {
                        setFormObj(defaultFormObj);
                      }
                    }}
                    shouldFilter={false}
                    input={search}
                  />
              }
              <ActionAndInfo value={
                {
                  id: formObj.item_id,
                  type: formObj.type,
                }
              } />
            </div>
          </div>
          <hr className="" />
          <div>
            <p className="text-sm font-semibold mb-2">
              Data Matrix Scan
            </p>
            <div
              className='space-y-2'
            >
              <Input
                placeholder="Scan data matrix code"
                disabled={false}
                value={dataMatrixCode || ""}
                onChange={(e) => {
                  setDataMatrixCode(e.target.value);
                }}
              />
            </div>
          </div>
          <hr className="" />
          <div>
            <p className="text-sm font-semibold mb-2">
              Batch No <span className="text-red-400">*</span>
            </p>
            <div
              className='space-y-2'
            >
              <Input
                placeholder="Enter batch number"
                disabled={formDisabled}
                value={formObj.batchNo}
                onChange={(e) => {
                  setFormObj({
                    ...formObj,
                    batchNo: e.target.value
                  })
                }}
              />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">
              Expiry Date <span className="text-red-400">*</span>
            </p>
            <div className='space-y-2'>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    disabled={formDisabled}
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !formObj.expiryDate && "text-muted-foreground"
                    )}
                  >
                    {formObj.expiryDate ? (
                      format(formObj.expiryDate, "PPP")
                    ) : (
                      <span>Expiry date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={new Date(formObj.expiryDate!)}
                    fromDate={(() => {
                      const today = dayjs().startOf('day');
                      if (formObj.productionDate) {
                        const productionDate = dayjs(formObj.productionDate).startOf('day');
                        if (productionDate.isAfter(today)) {
                          return productionDate.toDate();
                        }
                      }
                      return today.toDate();
                    })()}
                    onSelect={(newDate) => {
                      setFormObj({
                        ...formObj,
                        expiryDate: newDate?.toString()
                      })
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">
              Production Date
            </p>
            <div className='space-y-2'>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    disabled={formDisabled}
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !formObj.productionDate && "text-muted-foreground"
                    )}
                  >
                    {formObj.productionDate ? (
                      format(formObj.productionDate, "PPP")
                    ) : (
                      <span>Production Date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={new Date(formObj.productionDate!)}
                    toDate={dayjs().startOf('day').toDate()}
                    onSelect={(newDate) => {
                      let expiryDateForm = formObj.expiryDate;
                      if (formObj.expiryDate) {
                        const expiryDate = dayjs(formObj.expiryDate).startOf('day');
                        const productionDate = dayjs(newDate).startOf('day');
                        if (productionDate.isAfter(expiryDate)) {
                          expiryDateForm = undefined
                        }
                      }
                      setFormObj({
                        ...formObj,
                        expiryDate: expiryDateForm,
                        productionDate: newDate?.toString()
                      })
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
          <p className="text-sm font-semibold mb-2">
            Serial Number
          </p>
          <div
            className='space-y-2'
          >
            <Input
              placeholder="Enter serial number"
              disabled={formDisabled}
              value={formObj.serial_number || ""}
              onChange={(e) => {
                setFormObj({
                  ...formObj,
                  serial_number: e.target.value
                })
              }}
            />
          </div>
        </div>
          <hr className='my-2' />
          <div>
            <p className="text-sm font-semibold mb-2">
              Original <span className="text-red-400">*</span>
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter original quantity"
                disabled={formDisabled}
                value={formObj.quantity.strValue}
                onChange={(e) => {
                  stringToNumericString(e.target.value, (value) => {
                    setFormObj({
                      ...formObj,
                      quantity: value
                    })
                  });
                }}
              />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">
              Bonus
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter bonus quantity"
                disabled={formDisabled}
                value={formObj.bonus.strValue}
                onChange={(e) => {
                  stringToNumericString(e.target.value, (value) => {
                    setFormObj({
                      ...formObj,
                      bonus: value
                    })
                  });
                }}
              />
            </div>
          </div>
          <hr className='my-2' />
          <div>
            <p className="text-sm font-semibold mb-2">
              Cost <span className="text-red-400">*</span>
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter seller cost"
                value={formObj.cost.strValue}
                disabled={formDisabled}
                onChange={(e) => {
                  stringToNumericString(e.target.value, (value) => {
                    setFormObj({
                      ...formObj,
                      cost: value
                    })
                  });
                }}
              />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">
              Price <span className="text-red-400">{formObj.type === "NON_MEDICAL" ? "*" : ""}</span>
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter selling price"
                value={formObj.price.strValue}
                disabled={formDisabled || formObj.type === "MEDICAL"}
                onChange={(e) => {
                  stringToNumericString(e.target.value, (value) => {
                    setFormObj({
                      ...formObj,
                      price: value
                    })
                  });
                }}
              />
            </div>
          </div>
          <hr className='my-2' />
          <div>
            <p className="text-sm font-semibold mb-2">
              Custom Barcode
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter custom barcode"
                disabled={formDisabled || customBarcodeGenerating}
                value={formObj.customBarcode}
                onChange={(e) => {
                  setFormObj({
                    ...formObj,
                    customBarcode: e.target.value
                  })
                }}
              />
            </div>
            <div className="flex justify-end">
            <Button variant={"outline"} size={"sm"} onClick={async () => {
              try {
                setCustomBarcodeGenerating(true)
                
                // Generate barcode using existing API
                let generatedBarcode = "";
                if (formObj.type === "MEDICAL") {
                  const response = await pharmacyClient.generateCustomBarcode({
                    masterId: formObj.item_id,
                    itemType: ItemType.MEDICAL
                  });
                  generatedBarcode = response.barcode as string;
                } else {
                  const response = await pharmacyClient.generateCustomBarcode({
                    masterId: formObj.item_id,
                    itemType: ItemType.NON_MEDICAL
                  });
                  generatedBarcode = response.barcode as string;
                }
                
                // Update form with generated barcode
                setFormObj({
                  ...formObj,
                  customBarcode: generatedBarcode
                });
                
                // Open barcode modal with pre-populated data
                setBarcodeModalOpen(true);
                
              } catch (e) {
                alert("Failed to generate barcode")
                console.error(e)
              } finally {
                setCustomBarcodeGenerating(false)
              }
            }} disabled={formDisabled || customBarcodeGenerating}>{customBarcodeGenerating ? "Please Wait..." : "Generate & Print Barcode"}</Button>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mt-4 mb-2">
              Discount Type
            </p>
            <div className='space-y-2'>
              <Select
                value={formObj.discountType}
                onValueChange={(value: string) => {
                  const discountType = value.toString();
                  let discountValue = formObj.discountValue;
                  if (discountType === DiscountType.NONE.toString()) {
                    discountValue = numericStringDefault();
                  }
                  setFormObj({
                    ...formObj,
                    discountType: discountType,
                    discountValue: discountValue
                  })
                }}
                disabled={formDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Discount Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={
                    DiscountType.NONE.toString()
                  }>None</SelectItem>
                  <SelectItem value={
                    DiscountType.PERCENTAGE.toString()
                  }>Percentage</SelectItem>
                  <SelectItem value={
                    DiscountType.FIXED.toString()
                  }>Fixed</SelectItem>
                  <SelectItem value={
                    DiscountType.ALWAYS_FIXED.toString()
                  }>Always Fixed</SelectItem>
                  <SelectItem value={
                    DiscountType.ALWAYS_PERCENTAGE.toString()
                  }>Always Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">
              Discount Value
            </p>
            <div className='space-y-2'>
              <Input
                placeholder="Enter discount value"
                value={formObj.discountValue.strValue}
                onChange={(e) => {
                  stringToNumericString(e.target.value, (value) => {
                    setFormObj({
                      ...formObj,
                      discountValue: value
                    })
                  })
                }}
                disabled={
                  formDisabled || formObj.discountType === "NONE" || formObj.discountType === undefined
                }
              />
            </div>
          </div>
          <hr />
          <div className='flex items-center justify-between'>
            <p className="text-sm font-semibold">
              Tax Profile
            </p>
            {formObj.taxProfile.applicableTaxes.map((tax, index) => (
              <span key={index} className="text-sm text-gray-500">
                {tax.componentCode} ({tax.ratePercent}%)
                {index < formObj.taxProfile.applicableTaxes.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
          <Button type="submit" className="bg-blue-700"
            disabled={!formValidity}
          >
            Confirm & {
              mode === "ADD" ? "Add" : "Save"
            }
          </Button>
          <Button type="button" variant={'secondary'} className="mt-4" onClick={resetForm}>
            Cancel
          </Button>
          <BarcodeModal
            open={barcodeModalOpen}
            onOpenChange={setBarcodeModalOpen}
            initialData={{

              pharmacyName: getPharmacyID(),
              expiryDate: formObj.expiryDate ? new Date(formObj.expiryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              barcodeNumber: formObj.customBarcode,
              productName: formObj.trade_name

            }}
            onPrintComplete={(barcodeNumber) => {
              // Update form with the final barcode number
              setFormObj({
                ...formObj,
                customBarcode: barcodeNumber
              });
            }}
          />

        </form>
      }  </div>
  );
};

export default Add;